import React from "react";
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";

const steps = [
  { label: "Recipe", shortLabel: "Recipe" },
  { label: "Chef", shortLabel: "Chef" },
  { label: "Dietary", shortLabel: "Dietary" },
  { label: "Review", shortLabel: "Review" },
];

const StepIndicator: React.FC<{ currentStep: number }> = ({ currentStep }) => {
  const navigate = useNavigate();
  
  const handleStepClick = (stepNum: number) => {
    if (stepNum < currentStep && stepNum >= 1 && stepNum <= 4) {
      navigate(`/studio/step/${stepNum}`);
    }
  };

  // Calculate progress percentage for the bar
  const progressPercent = ((currentStep - 1) / (steps.length - 1)) * 100;

  return (
    <nav aria-label="Progress" className="w-full px-4 pt-4 pb-2">
      {/* Mobile: Compact progress bar with current step label */}
      <div className="md:hidden">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-gray-600 dark:text-gray-300">
            Step {currentStep} of {steps.length}
          </span>
          <span className="text-sm font-bold text-orange-600 dark:text-orange-400">
            {steps[currentStep - 1]?.label}
          </span>
        </div>
        <div className="relative h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div 
            className="absolute inset-y-0 left-0 bg-gradient-to-r from-orange-500 to-pink-500 rounded-full transition-all duration-500 ease-out"
            style={{ width: `${progressPercent}%` }}
          />
          {/* Step markers */}
          <div className="absolute inset-0 flex justify-between items-center px-0">
            {steps.map((_, idx) => {
              const stepNum = idx + 1;
              const isComplete = stepNum < currentStep;
              const isActive = stepNum === currentStep;
              const position = (idx / (steps.length - 1)) * 100;
              return (
                <div
                  key={idx}
                  className={`w-3 h-3 rounded-full border-2 transition-all duration-300 ${
                    isComplete 
                      ? 'bg-orange-500 border-orange-500' 
                      : isActive 
                        ? 'bg-white border-orange-500 shadow-md' 
                        : 'bg-gray-300 dark:bg-gray-600 border-gray-300 dark:border-gray-600'
                  }`}
                  style={{ position: 'absolute', left: `calc(${position}% - 6px)` }}
                />
              );
            })}
          </div>
        </div>
      </div>

      {/* Desktop: Horizontal stepper with dots and labels */}
      <div className="hidden md:flex justify-center">
        <div className="flex items-center w-full max-w-xl">
          {steps.map((step, idx) => {
            const stepNum = idx + 1;
            const isActive = stepNum === currentStep;
            const isComplete = stepNum < currentStep;
            const isClickable = isComplete;
            const isLast = idx === steps.length - 1;

            return (
              <React.Fragment key={stepNum}>
                {/* Step circle */}
                <div className="flex flex-col items-center">
                  <button
                    type="button"
                    disabled={!isClickable}
                    onClick={() => handleStepClick(stepNum)}
                    className={`flex items-center justify-center w-9 h-9 rounded-full border-2 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-400
                      ${isActive 
                        ? 'bg-gradient-to-r from-orange-500 to-pink-500 text-white border-orange-500 shadow-lg scale-110' 
                        : isComplete 
                          ? 'bg-gradient-to-r from-orange-400 to-pink-400 text-white border-orange-400 cursor-pointer hover:scale-105' 
                          : 'bg-white dark:bg-gray-800 text-gray-400 border-gray-300 dark:border-gray-600'
                      }`}
                    aria-current={isActive ? "step" : undefined}
                    tabIndex={isClickable ? 0 : -1}
                    aria-label={isClickable ? `Go back to ${step.label}` : step.label}
                  >
                    {isComplete ? (
                      <Check className="w-4 h-4" strokeWidth={3} />
                    ) : (
                      <span className="font-bold text-sm">{stepNum}</span>
                    )}
                  </button>
                  <span
                    className={`mt-1.5 text-xs font-medium transition-colors duration-300 ${
                      isActive 
                        ? 'text-orange-600 dark:text-orange-400 font-semibold' 
                        : isComplete 
                          ? 'text-gray-600 dark:text-gray-300' 
                          : 'text-gray-400 dark:text-gray-500'
                    }`}
                  >
                    {step.shortLabel}
                  </span>
                </div>

                {/* Connector line */}
                {!isLast && (
                  <div className="flex-1 mx-2 h-0.5 mt-[-18px]">
                    <div 
                      className={`h-full rounded transition-all duration-500 ${
                        isComplete 
                          ? 'bg-gradient-to-r from-orange-400 to-pink-400' 
                          : 'bg-gray-200 dark:bg-gray-700'
                      }`}
                    />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </nav>
  );
};

export default StepIndicator;
